import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { api } from '@api/v1/client'
import DataGrid from '@components/common/DataGrid'
import useDocumentTitle from '@hooks/useDocumentTitle'
import useReadState from '@hooks/useReadState'

const Invoices = () => {
  useDocumentTitle('Invoices')
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')

  const invoicesQuery = useQuery({
    queryKey: ['invoices-list', searchTerm],
    queryFn: () => api.get('/objects/30', {
      limit: 500,
      ...(searchTerm.length >= 2 ? { q: searchTerm } : {}),
    }),
  })

  /*
   * CC-53132 — this page was the worst case in the family, and not only because
   * of the paused read.
   *
   * It handed `<DataGrid>` seven props the grid does not have: `title`,
   * `subtitle`, `loading`, `error`, `searchTerm`, `onSearchChange` and
   * `searchPlaceholder`. `DataGrid` destructures a fixed list and has no rest
   * parameter, so all seven were dropped on the floor. The consequences were not
   * cosmetic: with `loading` never reaching `isLoading` the page never showed a
   * spinner, and with `error` never reaching anything at all it rendered "No
   * invoices found" for EVERY failure — a 500, a dead session, a timeout — on the
   * screen a biller uses to decide what is outstanding. "No unpaid invoices" when
   * the truth is "we never asked" is the same class of defect as a wrong number.
   *
   * On top of that, `data?.data || []` collapsed the third outcome. `isLoading`
   * is `isPending && isFetching`, so it is FALSE for a query that is pending but
   * NOT in flight — above all a PAUSED one (react-query's default
   * `networkMode: 'online'` while `onlineManager` believes the browser is
   * offline). `queryFn` is never called, so no request leaves the browser:
   * nothing throws, nothing is logged, and there is nothing to find in a log on
   * either side. `data` just stays `undefined` forever.
   *
   * `useReadState` rather than the bare `readState` because it carries the
   * permanent blocked-read diagnostic, which is the only trace such a read leaves
   * anywhere.
   */
  const invoicesState = useReadState('invoices-list', invoicesQuery)
  const readReady = invoicesState === 'ready'

  // Deliberately NOT `|| []`: that idiom IS the bug. `undefined` has to reach
  // `DataGrid`, which tells it apart from `[]` (`Array.isArray`) and renders its
  // honest role="alert" message instead of `emptyMessage` — and renders no row
  // count. It is also a plain property read off the cached response, so it is
  // stable across renders, which the `|| []` it replaces was not.
  const bills = invoicesQuery.data?.data
  /*
   * The count is a claim, and this one is the most quotable number on a billing
   * page. It may only be made about a read that finished; until then it is
   * `undefined` and the subtitle does not render at all rather than saying
   * "0 invoices". A resolved response carrying no rows array either (the
   * CC-50321 shape problem) is not a zero, so `??` — not `||` — bottoms out at
   * `undefined` there too.
   */
  const total = readReady ? (invoicesQuery.data?.meta?.total ?? bills?.length) : undefined

  const columns = useMemo(() => [
    {
      id: 'invoiceNumber', header: 'Invoice #', accessorKey: 'invoiceNumber', size: 120,
      cell: ({ getValue }) => (
        <span className="font-medium text-cb-accent">{getValue() || '—'}</span>
      ),
    },
    {
      id: 'matter', header: 'Matter', accessorKey: 'matter', size: 250,
      cell: ({ getValue }) => (
        <span className="text-gray-700 truncate block max-w-[250px]">{getValue() || '—'}</span>
      ),
    },
    {
      id: 'attorney', header: 'Attorney', accessorKey: 'attorney', size: 150,
    },
    {
      id: 'issueDate', header: 'Date', accessorKey: 'issueDate', size: 120,
      cell: ({ getValue }) => {
        const v = getValue()
        return v ? dayjs(v).format('MMM D, YYYY') : '—'
      },
    },
    {
      id: 'status', header: 'Status', accessorKey: 'status', size: 120,
      cell: ({ getValue }) => {
        const s = getValue() || '—'
        const color = s === 'Posted' ? 'bg-green-50 text-green-700'
          : s === 'Pending' ? 'bg-amber-50 text-amber-700'
          : 'bg-gray-100 text-gray-600'
        return (
          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>
            {s}
          </span>
        )
      },
    },
    {
      id: 'invoiceTotal', header: 'Total', accessorKey: 'invoiceTotal', size: 120,
      cell: ({ getValue }) => {
        const v = parseFloat(getValue())
        return <span className="font-medium">{isNaN(v) ? '—' : `$${v.toFixed(2)}`}</span>
      },
      meta: { align: 'right' },
    },
    {
      id: 'balance', header: 'Balance', accessorKey: 'balance', size: 120,
      cell: ({ getValue }) => {
        const v = parseFloat(getValue())
        return <span className={v > 0 ? 'font-semibold text-gray-900' : 'text-gray-500'}>{isNaN(v) ? '—' : `$${v.toFixed(2)}`}</span>
      },
      meta: { align: 'right' },
    },
  ], [])

  return (
    <div className="max-w-7xl mx-auto">
      {/* The title and the count used to be `title` / `subtitle` props on
          `DataGrid`, which has neither, so neither has ever rendered. They belong
          to the page — this is the same header every other list page here owns
          (Matters, ObjectList) — and the count is now gated on a finished read. */}
      <div className="mb-6">
        <h1 className="text-2xl font-display text-gray-900">Invoices</h1>
        {total != null && (
          <p className="text-sm text-gray-500 mt-1">
            {total} {total === 1 ? 'invoice' : 'invoices'}
          </p>
        )}
      </div>

      {/* Same story as the title: `searchTerm` / `onSearchChange` /
          `searchPlaceholder` were passed to a grid that reads none of them, so
          the box never existed and `searchTerm` could never leave ''. The wiring
          behind it is real and server-side (`q` → cb-api `/objects/:typeId`,
          routes.js:93), so the honest repair is to render the control rather than
          delete a working capability or leave inert props lying for the next
          reader. Markup and aria-label follow Matters.jsx, the list page with the
          same server-search shape. It also matters to THIS ticket: a search read
          is the same query, so a paused or failed search must not tell a biller
          their search matched nothing. */}
      <div className="mb-6 max-w-md relative">
        <svg
          className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500"
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
        >
          <path
            strokeLinecap="round" strokeLinejoin="round"
            d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
          />
        </svg>
        <input
          type="text"
          aria-label="Search invoices"
          placeholder="Search invoices..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200
            rounded-lg text-sm text-gray-900 placeholder:text-gray-500
            focus:outline-none focus:ring-2 focus:ring-cb-accent/30
            focus:border-cb-accent"
        />
      </div>

      {/* The state that used to render as "No invoices found". react-query PAUSED
          the fetch, so there is no request, no error and — because `isLoading` is
          false for a paused query — not even a spinner. A spinner would be barely
          better: a paused query never resolves on its own. Say what happened and
          give a way out; `refetch` resumes the moment the connection returns. */}
      {invoicesState === 'blocked' ? (
        <div role="alert" className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-900">
          <p className="font-medium">Can't reach the server, so we haven't loaded your invoices.</p>
          <p className="mt-1 text-amber-800">
            This is not an empty list, and it does not mean nothing is outstanding — no invoices
            were read at all. Your connection looks offline; check it and try again.
          </p>
          <button
            type="button"
            onClick={() => invoicesQuery.refetch()}
            className="mt-3 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
          >
            Try again
          </button>
        </div>
      ) : invoicesState === 'failed' ? (
        /* A failure had NO render site on this page before — `error` went to a
           prop that does not exist — so every 500, expired session and timeout
           came out as "No invoices found". A user shown the empty state has no
           reason to retry: they conclude the firm is paid up. */
        <div role="alert" className="rounded-xl border border-red-100 bg-red-50 p-6 text-sm text-red-700">
          <p className="font-medium">
            Couldn't load invoices: {invoicesQuery.error?.message || 'the server did not answer.'}
          </p>
          <p className="mt-1">
            This is not an empty list, and it does not mean nothing is outstanding — no invoices
            were read at all.
          </p>
          <button
            type="button"
            onClick={() => invoicesQuery.refetch()}
            className="mt-3 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100"
          >
            Try again
          </button>
        </div>
      ) : (
        /* `loading`, `idle` and `ready` land here.

           `idle` rides with `loading` ON PURPOSE. It means pending-but-never-
           started, i.e. `enabled: false`, and this query has no `enabled` option
           at all, so it is unreachable — folding it in therefore cannot strand
           the page on a permanent spinner. It is covered anyway because every
           branch above is gated on a named state, and an unhandled fifth state
           renders NOTHING: the first version of this fix shipped a blank catalog
           page exactly that way. Anyone adding an `enabled` gate here later gets
           a spinner instead of a blank screen, and should revisit this comment. */
        <DataGrid
          columns={columns}
          data={bills}
          isLoading={invoicesState === 'loading' || invoicesState === 'idle'}
          onRowClick={(row) => navigate(`/invoices/${row.id}`)}
          emptyMessage="No invoices found"
          /* Reachable on this path only for a "ready" response that carried no
             rows array — a shape we could not read, which is not a zero. */
          unresolvedMessage="These invoices could not be loaded, so this is not known to be an empty list. Reload to try again."
          loadingMessage="Loading invoices..."
        />
      )}
    </div>
  )
}

export default Invoices
